/**
 * Reels — the archive as a film somebody plays in a room.
 *
 * The one artefact this application makes that leaves it. Every other view is
 * a way of looking at the archive from inside the archive; a reel is a file
 * somebody puts on a television at a wake, which is why it is kept as a row
 * rather than produced and forgotten. Nobody wants to rebuild one from memory
 * an hour before a service.
 *
 * ## Made from whatever is narrowed
 *
 * There is no separate selection to build up. The filter is the selection — a
 * person, a date range, an album, a search — because that is the machinery the
 * whole client already shares and because "the ones of Mum in the seventies"
 * is a filter, not a list somebody should have to assemble by clicking forty
 * times.
 */

import { h, replace } from "../dom";
import { api } from "../api";
import { store } from "../state";
import { empty, failure, toast } from "../ui";
import { ask } from "../modal";
import { bytes, count } from "../format";
import { videoPlayer } from "../videoplayer";
import type { Face, ReelSummary } from "../types";
import type { View } from "./view";

/**
 * The shapes people actually want, rather than a grid of numbers.
 *
 * A reel is watched on a television or a phone and there is no third answer,
 * so the choice is which of those — not a width and a height, which is a
 * question about video encoding dressed up as a question about a funeral.
 */
const SHAPES: Array<[string, string, { width: number; height: number }]> = [
  ["wide", "For a television (16:9)", { width: 1920, height: 1080 }],
  ["tall", "For a phone (9:16)", { width: 1080, height: 1920 }],
  ["square", "Square, for social", { width: 1080, height: 1080 }],
];

/**
 * How each photograph is presented, and whether there is anywhere to write.
 *
 * A caption goes on the chin of a Polaroid — `composite_captioned` draws
 * nothing unless the frame style *is* a Polaroid, because without something
 * written on it a Polaroid just looks lopsided. So the mount and the caption
 * are one choice, not two: offering a font when there is no chin to write on
 * would be a picker that silently does nothing, which is the failure this
 * whole exercise started from.
 */
const MOUNTS: Array<[string, string, Record<string, unknown>]> = [
  ["none", "Full frame, no border", { frame: "none", frame_caption: false }],
  ["plain", "On a white border", { frame: "medium", frame_style: "plain", frame_caption: false }],
  [
    "polaroid",
    "Like a Polaroid, written on",
    { frame: "medium", frame_style: "polaroid", frame_caption: true },
  ],
];

const PACES: Array<[string, string, number]> = [
  ["slow", "Slow — 7 seconds each", 7],
  ["steady", "Steady — 5 seconds each", 5],
  ["brisk", "Brisk — 3 seconds each", 3],
];

/** What a face's kind is called in the picker. */
function kindWord(kind: string): string {
  switch (kind) {
    case "hand":
      return "handwriting";
    case "serif":
      return "serif";
    case "sans":
      return "plain";
    default:
      return kind;
  }
}

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function build(): Promise<void> {
    // Whatever is narrowed right now, in the order the archive would show it.
    let ids: string[];
    let total: number;
    try {
      const slice = await api.cards(store.libraryId, store.filter, { limit: 400 });
      ids = slice.items.map((i) => i.id);
      total = ids.length;
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not read the archive", true);
      return;
    }
    if (!total) {
      toast("Nothing is selected. Narrow the archive first, then come back.", true);
      return;
    }

    // The faces this archive can be captioned in. Asked before the dialog
    // opens rather than after: a picker that fills in a second later is a
    // picker somebody has already scrolled past.
    let faces: Face[] = [];
    try {
      faces = (await api.faces(store.libraryId)).fonts;
    } catch {
      // No list means no picker, and the reel is captioned in whatever the
      // renderer falls back to — which is the behaviour there has always been.
    }

    /**
     * A short stretch, before committing minutes.
     *
     * Four photographs at 480px, rendered while you wait. The question a
     * preview answers is "is the shape and the pace right", and neither needs
     * the whole reel or the full resolution to answer — a preview you have to
     * come back for is not a preview.
     */
    const preview = h("div", { class: "previewbox" });
    let previewUrl: string | null = null;
    const previewRequests = new Set<AbortController>();

    /**
     * The chosen face, drawn in the chosen face.
     *
     * A list of nine names is nine names; the same nine rendered in their own
     * letters is a choice somebody can actually make. The family comes from
     * the catalogue rather than from the label, because the browser and the
     * renderer have to pick the *same* face or this is decoration.
     */
    const sample = h("div", { class: "fontsample" });
    const drawSample = (): void => {
      const chosen = faces.find((f) => f.id === current("font")) ?? faces[0];
      if (!chosen) {
        sample.hidden = true;
        return;
      }
      sample.hidden = false;
      sample.setAttribute("style", `font-family:${chosen.css}`);

      // Said rather than discovered: with any other mount there is no chin,
      // `composite_captioned` draws nothing, and a sample promising otherwise
      // would be the lie this feature exists to remove.
      const writable = current("mount") === "polaroid";
      sample.classList.toggle("muted", !writable);
      replace(
        sample,
        h("span", { class: "fs-line", text: "Nana and the girls" }),
        h("span", { class: "fs-year", text: "1961" }),
        writable
          ? null
          : h("span", { class: "fs-note", text: "no caption without the Polaroid mount" }),
      );
    };
    const previewBtn = h("button", {
      class: "btn sm",
      type: "button",
      text: "Show me a few seconds",
      onclick: () => {
        void (async () => {
          for (const active of previewRequests) active.abort();
          previewRequests.clear();
          const request = new AbortController();
          previewRequests.add(request);
          const shape = SHAPES.find((x) => x[0] === current("shape"))?.[2] ?? SHAPES[0]![2];
          const pace = PACES.find((x) => x[0] === current("pace"))?.[2] ?? 5;
          replace(preview, h("div", { class: "loading", text: "· · · rendering a few seconds" }));
          try {
            const res = await fetch(api.reelPreviewUrl(store.libraryId), {
              method: "POST",
              signal: request.signal,
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                items: ids.slice(0, 4),
                style: {
                  ...shape,
                  ...(MOUNTS.find((m) => m[0] === current("mount"))?.[2] ?? {}),
                  seconds_per_photo: pace,
                  ...(current("font") ? { caption_font: current("font") } : {}),
                },
              }),
            });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.blob();
            if (request.signal.aborted) return;
            const url = URL.createObjectURL(blob);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            previewUrl = url;
            replace(
              preview,
              videoPlayer({
                className: "previewplayer",
                src: url,
                autoplay: true,
                loop: true,
                muted: true,
              }).el,
            );
          } catch (e) {
            if (request.signal.aborted) return;
            replace(
              preview,
              h("p", {
                class: "sub",
                text: e instanceof Error ? e.message.slice(0, 160) : "the preview did not render",
              }),
            );
          } finally {
            previewRequests.delete(request);
          }
        })();
      },
    });

    // Read out of the open dialog, because the preview is rendered from what
    // is chosen *now* rather than from what was chosen when it opened.
    const current = (field: string): string => {
      const el = document.querySelector<HTMLSelectElement>(`dialog[open] [name="${field}"]`);
      return el?.value ?? "";
    };

    // The dialog is not in the document until `ask` renders it, so the first
    // sample and the change listener are attached on the next tick.
    setTimeout(() => {
      drawSample();
      for (const field of ["font", "mount"]) {
        document
          .querySelector<HTMLSelectElement>(`dialog[open] [name="${field}"]`)
          ?.addEventListener("change", drawSample);
      }
    }, 0);

    const answer = await ask({
      title: "Make a reel",
      body:
        `${count(total, "photograph")} from what is narrowed now, oldest first. ` +
        "It renders in the background — you can close this and carry on.",
      fields: [
        {
          name: "name",
          label: "Called",
          placeholder: "For Nana's service",
          required: true,
          maxLength: 120,
        },
        { name: "shape", label: "Shape", options: SHAPES.map(([v, l]) => [v, l]), value: "wide" },
        { name: "pace", label: "Pace", options: PACES.map(([v, l]) => [v, l]), value: "steady" },
        {
          name: "mount",
          label: "Each one",
          options: MOUNTS.map(([v, l]) => [v, l]) as Array<[string, string]>,
          value: "polaroid",
          hint: "A Polaroid has a chin to write the caption and the year on.",
        },
        ...(faces.length
          ? [
              {
                name: "font",
                label: "Written in",
                // Grouped the way the catalogue groups them, so the three
                // kinds are distinguishable in a flat list of nine.
                options: faces.map((f) => [f.id, `${f.label} · ${kindWord(f.kind)}`]) as Array<
                  [string, string]
                >,
                value: faces.find((f) => f.kind === "hand")?.id ?? faces[0]!.id,
                hint: "On the chin, with the year. Needs the Polaroid mount.",
              },
            ]
          : []),
        {
          name: "choose",
          label: "Which ones",
          options: [
            ["all", `All ${total}, in order`],
            ["decade", "Fewer, spread evenly across the decades"],
            ["person", "Fewer, sharing time between the people in them"],
          ],
          value: "all",
          hint:
            "An archive holds four hundred photographs from last year and eleven " +
            "from the forties. Spreading them keeps the irreplaceable ones.",
        },
      ],
      confirm: "Build it",
      preview: h("div", { class: "previewwrap" }, sample, previewBtn, preview),
    });
    for (const request of previewRequests) request.abort();
    previewRequests.clear();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const name = answer?.name?.trim();
    if (!name) return;

    const shape = SHAPES.find((x) => x[0] === answer?.shape)?.[2] ?? SHAPES[0]![2];
    const pace = PACES.find((x) => x[0] === answer?.pace)?.[2] ?? 5;

    // Which of them, if not all. `curate` is the desktop's own selection —
    // decades weighted evenly for a life, people weighted evenly for a reel
    // about siblings — and it reports the decade breakdown so the choice can
    // be explained rather than just made.
    let items = ids;
    if (answer?.choose === "decade" || answer?.choose === "person") {
      const target = Math.max(12, Math.min(80, Math.round(total / 3)));
      try {
        const chosen = await api.curate(store.libraryId, target, answer.choose);
        if (chosen.picks.length) {
          items = chosen.picks.map((pk) => pk.assetId);
          const kept = chosen.decades
            .filter((d) => d.selected)
            .map((d) => `${d.decade}s: ${d.selected} of ${d.available}`)
            .join(", ");
          toast(`Chose ${count(items.length, "photograph")}. ${kept}`);
        }
      } catch (e) {
        toast(
          e instanceof Error
            ? `${e.message} — using all of them instead`
            : "could not choose, using all of them",
          true,
        );
      }
    }

    try {
      const mount = MOUNTS.find((m) => m[0] === answer?.mount)?.[2] ?? MOUNTS[2]![2];
      await api.makeReel(store.libraryId, name, items, {
        ...shape,
        ...mount,
        // The field name is `heirloom-core`'s. `font_by_id` falls back rather
        // than refusing, so a face the render cannot load gives a caption in
        // the wrong letters instead of a reel with blank chins.
        ...(answer?.font ? { caption_font: answer.font } : {}),
        // snake_case, because the field names are `heirloom-core`'s and serde
        // uses the Rust names. Getting this wrong would not fail — serde would
        // fill the default and the reel would silently run at 5 seconds a
        // photograph whatever was chosen.
        seconds_per_photo: pace,
      });
      toast("Building. It will appear here when it is ready.");
      void refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not start it", true);
    }
  }

  void build;

  function reelEl(r: ReelSummary, again: () => void): HTMLElement {
    const ready = r.state === "ready";
    const failed = r.state === "failed";

    return h(
      "div",
      { class: `reel ${r.state}` },
      ready
        ? videoPlayer({
            className: "reelplayer",
            src: api.reelVideoUrl(store.libraryId, r.id),
          }).el
        : h(
            "div",
            { class: "reelwait" },
            h("span", {
              class: "st",
              text: failed ? "could not be made" : r.state === "rendering" ? "rendering" : "queued",
            }),
            h("span", { class: "de", text: r.detail ?? "" }),
          ),
      h(
        "div",
        { class: "reelmeta" },
        h("h3", { text: r.name }),
        h("div", {
          class: "d",
          text: [
            count(r.count, "photograph"),
            r.byteSize ? bytes(r.byteSize) : null,
            r.createdAt.replace("T", " ").slice(0, 16),
          ]
            .filter(Boolean)
            .join(" · "),
        }),
        h(
          "div",
          { class: "reelacts" },
          ready
            ? h(
                "a",
                {
                  class: "btn sm",
                  href: api.reelVideoUrl(store.libraryId, r.id),
                  download: `${r.name.replace(/[^\w -]/g, "")}.mp4`,
                },
                "Save the file",
              )
            : null,
          store.can("reel.render")
            ? h("button", {
                class: "btn sm ghost danger",
                text: "Delete",
                title: "The reel, not the photographs",
                onclick: () => {
                  void api
                    .deleteReel(store.libraryId, r.id)
                    .then(() => {
                      toast("Deleted. The photographs are untouched.");
                      again();
                    })
                    .catch((e: unknown) =>
                      toast(e instanceof Error ? e.message : "could not delete it", true),
                    );
                },
              })
            : null,
        ),
      ),
    );
  }

  async function refresh(): Promise<void> {
    const mine = ++generation;
    clearTimeout(timer);

    let found: { reels: ReelSummary[] };
    try {
      found = await api.reels(store.libraryId);
    } catch (e) {
      if (mine !== generation) return;
      replace(pane, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    const makeBtn = store.can("reel.render")
      ? h("button", {
          class: "btn primary",
          text: "Make a reel",
          onclick: () => store.go("reel-editor"),
        })
      : null;

    if (!found.reels.length) {
      replace(
        pane,
        empty({
          icon: "film",
          title: "No reels yet",
          body: [
            "A reel is the archive as a film — photographs in order, with their " +
              "years under them, as one file somebody can play in a room.",
            "It is made from whatever is narrowed: a person, a stretch of years, " +
              "an album. Narrow the archive first and the reel is those " +
              "photographs, in that order.",
          ],
          actions: makeBtn ? [makeBtn] : [],
        }),
      );
      return;
    }

    const head = h(
      "div",
      { class: "gridhead reelhead" },
      h("h2", { text: "Reels" }),
      h("div", { class: "grow" }),
      ...(makeBtn ? [makeBtn] : []),
    );
    const list = h("div", { class: "reels" });
    for (const r of found.reels) list.appendChild(reelEl(r, () => void refresh()));
    replace(pane, head, list);

    // One poll while something is still cooking, and none once nothing is.
    // A reel takes minutes and a live socket message for every frame would be
    // a socket message for every frame.
    if (found.reels.some((r) => r.state === "queued" || r.state === "rendering")) {
      timer = setTimeout(() => void refresh(), 8000);
    }
  }

  void refresh();
  return {
    refresh: () => void refresh(),
    destroy() {
      generation++;
      clearTimeout(timer);
    },
  };
  // The routed studio owns the visible creation workflow; this view lists its results.
}

